import React from 'react';
import PropTypes from 'prop-types';

import createBreakpoints from '../../context/theme/functions/create-breakpoints';
import { isBreakpoint } from '../../utilities';

/** PROP-TYPES */
const sharedPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

const visibleOnPropTypes = {
  ...sharedPropTypes,
  hiddenOn: PropTypes.bool,
};

const visibleOnDefaultProps = { hiddenOn: false };

/**
 * Visible On Component
 * This component is used to conditionally render child components dependant
 * on the screen size using breakpoints
 *
 * @param {*} { children, hiddenOn, ...otherProps }
 * @returns
 */
const VisibleOn = ({ children, hiddenOn, ...otherProps }) => {
  // STATE
  const [isVisible, setIsVisible] = React.useState(false);

  // VARIABLES
  const { keys } = createBreakpoints();
  const childProps = Object.keys(otherProps)
    .filter(key => !keys.includes(key))
    .reduce(
      (prev, current) => ({ ...prev, [current]: otherProps[current] }),
      {},
    );

  // METHOD TO DETERMINE IF CHILDREN SHOULD BE RENDERED / VISIBLE
  const handleVisibility = () => {
    setIsVisible(hiddenOn);
    if (isBreakpoint(...Object.keys(otherProps))) setIsVisible(!hiddenOn);
  };

  // ON MOUNT - USEEFFECT
  React.useEffect(() => {
    handleVisibility();
    window.addEventListener('resize', handleVisibility);
    return () => window.removeEventListener('resize', handleVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isVisible ? React.cloneElement(children, { ...childProps }) : null;
};

/**
 * Hidden On Component
 * This component is an alias for VisibleOn and is used to prevent render of
 * child components based on screen size using breakpoints.
 *
 * @param {*} { children, ...otherProps }
 * @returns
 */
export const HiddenOn = ({ children, ...otherProps }) => {
  return (
    <VisibleOn {...otherProps} hiddenOn>
      {children}
    </VisibleOn>
  );
};

/** PROP-TYPES ASSIGNMENT */
VisibleOn.propTypes = visibleOnPropTypes;
VisibleOn.defaultProps = visibleOnDefaultProps;
HiddenOn.propTypes = { ...sharedPropTypes };

export default VisibleOn;
